import React from "react";
import {
    FormLayout,
    HeaderActions,
    Heading,
    Input,
    LoadingProgress,
    Panel,
    Text,
} from "@/ui";
import { Icon } from "@/components";
import useMeta from "../../base/hooks/useMeta";
import useForm from "../../base/hooks/form/useForm";
import useFormActions from "../../base/hooks/form/useFormActions";
import {
    MUTATION_UPDATE_COVA_APP,
    QUERY_LIST_STORE_APPS,
} from "../../apollo/module-operations/app";
import _ from "lodash";
import useValidator from "../../base/hooks/form/useValidator";
import styles from "./GeneralsForm.module.scss";

const SaveIcon = () => <Icon icon="save" />;

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const CovaForm = ({ app }) => {
    const { user } = useMeta();
    const { inputs, setInputs, inputChange } = useForm();

    if (!_.get(inputs, "id", null)) {
        setInputs(app);
    }

    let options = {
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
            input: {
                ...inputs,
                id: inputs.id,
                client_id: inputs.client_id,
                client_secret: inputs.client_secret,
                username: inputs.username,
                password: inputs.password,
                company_id: inputs.company_id,
                type: "Cova",
            },
        },
    };

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "id",
                method: "isEmpty",
                validWhen: false,
                message: "This app id is required.",
            },

            {
                field: "client_id",
                method: "isEmpty",
                validWhen: false,
                message: "This client id is required.",
            },

            {
                field: "client_secret",
                method: "isEmpty",
                validWhen: false,
                message: "This client secret is required.",
            },

            {
                field: "username",
                method: "isEmpty",
                validWhen: false,
                message: "This username is required.",
            },
            {
                field: "password",
                method: "isEmpty",
                validWhen: false,
                message: "This password is required.",
            },

            {
                field: "company_id",
                method: "isEmpty",
                validWhen: false,
                message: "This company id is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    const { createOrUpdate, loading } = useFormActions(
        "id",
        "Cova",
        "",
        MUTATION_UPDATE_COVA_APP,
        [
            {
                query: QUERY_LIST_STORE_APPS,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
                skip: user.loading || !user.accountId || !user.storeId,
            },
        ],
        options,
        "",
        isValid,
        isSubmited,
        "primary",
    );

    return (
        <Panel
            className={styles.root}
            header={
                <div className={styles.headerBar}>
                    <div className={styles.headerText}>
                        <Heading level={4}>Cova</Heading>
                        <Text tone="secondary" variant="body-sm">
                            Cova Information
                        </Text>
                    </div>
                    <HeaderActions
                        actions={[
                            {
                                id: "update",
                                label: "Update",
                                icon: loading ? undefined : <SaveIcon />,
                                loading,
                                disabled: loading,
                                onClick: () => createOrUpdate(),
                            },
                        ]}
                        aria-label="Cova actions"
                    />
                </div>
            }
            showHeaderDivider
        >
            {user.loading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <FormLayout>
                    <FormLayout.Section title="Information">
                        <FormLayout.Grid columns={2} maxWidth="twoThirds">
                            <Input
                                error={fieldError(validator, "client_id")}
                                fullWidth
                                label="Client Id"
                                name="client_id"
                                onChange={inputChange}
                                value={inputs.client_id || ""}
                            />
                            <Input
                                error={fieldError(validator, "client_secret")}
                                fullWidth
                                label="Client Secret"
                                name="client_secret"
                                onChange={inputChange}
                                value={inputs.client_secret || ""}
                            />
                            <Input
                                error={fieldError(validator, "username")}
                                fullWidth
                                label="Username"
                                name="username"
                                onChange={inputChange}
                                value={inputs.username || ""}
                            />
                            <Input
                                error={fieldError(validator, "password")}
                                fullWidth
                                label="Password"
                                name="password"
                                onChange={inputChange}
                                type="password"
                                value={inputs.password || ""}
                            />
                            <Input
                                error={fieldError(validator, "company_id")}
                                fullWidth
                                label="Company Id"
                                name="company_id"
                                onChange={inputChange}
                                value={inputs.company_id || ""}
                            />
                        </FormLayout.Grid>
                    </FormLayout.Section>
                </FormLayout>
            )}
        </Panel>
    );
};

export default CovaForm;
